/*
 * Copyright © 2020-2022 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-10-24 11:18:07
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Layout, Popover, Collapse, InputNumber, Button } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import './index.scss'
import MapContainer from './components/MapContainer'
import sensors from '../../utils/sensors'
import contact_us_icon from '../../images/contact_us.png'
import Filter from './components/Filter'
import dayjs from 'dayjs'
import { fetchPhotosMarkerData } from '../../actions/photoLocation'
import { useAppDispatch, useAppSelector } from '../../app/hooks'
import { SelectedDepartment } from '../components/DepartmentMemberPicker'
import POIDetailCard from './components/POIDetailCard'
import OpenVipModal from './components/OpenVipModal'
import ContactUS from '../components/ContactUS'
import actionTypes from '../../constants/actionTypes'

import member_location_entry_btn from '../memberLocation/images/member-location-entry-btn.png'
import { useNavigate } from 'react-router-dom'

import PageHeader from '@/components/PageHeader'
const { Panel } = Collapse
const { Content } = Layout
interface IProps {
  [key: string]: any
}
import type { Dayjs } from 'dayjs'
type times = {
  start: Dayjs
  end: Dayjs
}
export type districtsOptionType = {
  adcode: string
  citycode: string
  lat: string // 纬度
  level: string
  lng: string // 经度
  name: string
  invalid?: boolean // 代表此选项是否有效
}
export type FilterType = {
  users: string[]
  userIDList: string[]
  departmentIDList: SelectedDepartment[]
  times: times
  selectedDistricts: districtsOptionType
  invalid?: boolean // 标识此次筛选无效
}
// 地区概览数属性
export type AreaOverView = {
  adcode: string // 区域编码
  citycode: string //   城市编码
  lat: string // 纬度
  lng: string //   经度
  level: 'city' | 'district' | 'town' | 'poi' // 行政区域级别 city 城市，district 区/县， town 街道， poi 地点
  name: string // 行政区名称
  photoAddrCnt: number // 拍照地点数
  photoCnt: number // 拍照总数
  photoUserCnt: number // 拍照总人数
  subDistrictCnt?: number // 下一级的拍照区域数
  photoCover?: string // 拍照封面,
  photoTotalDate?: string | number // 累计拍照天数
  extra?: any // 额外附加的数据
}
// poi地点概览属性
export type POIItemOverView = AreaOverView & {
  addrName: string //  客户拜访地点名 || poi 名称,
  latestPhotoDay: number //  最近到访的天数
  latestPhotoDate: string // 最近拍照时间
  fmtAddr?: string // POI 详细地点
}

export type DistrictsStatistic = {
  districtOverView: AreaOverView
  poiStatistic: Array<POIItemOverView>
}

// 照片地点数据数据
export type PhotoAddrStatisticResp = {
  cityOverView: AreaOverView
  districtsStatistic: Array<DistrictsStatistic>
}

const defaultDateRange: times = {
  start: dayjs().add(-29, 'days').startOf('days'),
  end: dayjs().add(0, 'days').endOf('days')
}
export const convertMaxNumberStr = (number, max) => {
  return `${number > max ? max + '+' : number}`
}
const PhotoLocation: React.FC<IProps> = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  // 筛选项数据
  const [filterData, setFilterData] = useState<FilterType>({
    users: [],
    userIDList: [],
    departmentIDList: [],
    times: defaultDateRange,
    selectedDistricts: {} as districtsOptionType,
    invalid: false
  })
  // 地图统计数据
  const [mapData, setMapData] = useState<PhotoAddrStatisticResp>(
    {} as PhotoAddrStatisticResp
  )
  // 当前市/区县/poi 数据概览
  const [currentOverView, setCurrentOverView] = useState<
    AreaOverView | POIItemOverView
  >({} as AreaOverView)
  // 上一次选中的 市/区县 数据概览
  const prevOverView = useRef<AreaOverView | POIItemOverView>(
    {} as AreaOverView
  )
  const [poiDetailCardVisible, setPoiDetailCardVisible] = useState(false)
  const [openVipModalVisible, setOpenVipModalVisible] = useState(false)
  const groupState = useAppSelector((state) => state.group)
  const { isVip, groupId } = groupState
  const [maxPoint, setMaxPoint] = useState(9999)
  const showMemberLocation = groupState.show_member_location

  // 设置筛选数据
  const handleChangeFilter = useCallback((params: Partial<FilterType>) => {
    setFilterData((prevState) => ({
      ...prevState,
      ...params,
      invalid: params.invalid || false // 标识此次筛选是否无效，true 无效  false 有效
    }))
  }, [])

  // 筛选数据变化时，重新重新请求数据
  useEffect(() => {
    setOpenVipModalVisible(false)
    requestData(filterData)
  }, [filterData])

  // 设置上一次选中的 市/区县 数据概览
  useEffect(() => {
    if (
      currentOverView.level !== 'poi' &&
      currentOverView.adcode !== prevOverView.current.adcode
    ) {
      prevOverView.current = currentOverView
    }
  }, [currentOverView])

  // 点击上报
  const sensorsClick = useCallback(
    (clickItem, locationName = '') => {
      sensors.track('web_click_page_photo_location', {
        clickItem,
        locationName,
        groupID: groupId,
        isGroupVIP: isVip
      })
    },
    [isVip, groupId]
  )

  // 全量请求城市相关数据
  const requestData = useCallback(
    ({
      userIDList,
      users,
      departmentIDList,
      times = defaultDateRange,
      selectedDistricts,
      invalid
    }: FilterType) => {
      // 对于无效的筛选项，不需要发起请求
      if (invalid) return
      dispatch({
        type: actionTypes.SET_LOADING,
        data: true
      })
      const params = {
        userIDList,
        users,
        groupID: groupId,
        departmentIDList: departmentIDList.map((item) => item.id),
        startDate: times.start.format('YYYY-MM-DD'),
        endDate: times.end.format('YYYY-MM-DD'),
        districtLevel: selectedDistricts.level || 'city',
        districtName: selectedDistricts.name || ''
      }
      dispatch(fetchPhotosMarkerData(params))
        .then((res) => {
          dispatch({
            type: actionTypes.SET_LOADING,
            data: false
          })
          let result = {
            cityOverView: (res.cityOverView || {}) as AreaOverView,
            districtsStatistic: res.districtsStatistic || []
          }
          setMapData(result as PhotoAddrStatisticResp)
          // 首次请求地图数据没有传入城市，请求完成后手动选中该城市
          if (!selectedDistricts.adcode) {
            handleChangeFilter({
              invalid: true,
              selectedDistricts: {
                lng: result.cityOverView.lng,
                lat: result.cityOverView.lat,
                adcode: result.cityOverView.adcode,
                name: result.cityOverView.name,
                citycode: result.cityOverView.citycode,
                level: result.cityOverView.level
              }
            })
          }
          //  if (isVip && result.districtsStatistic.length === 1) {
          if (result.districtsStatistic.length === 1) {
            setCurrentOverView(result.districtsStatistic[0].districtOverView)
          } else {
            setCurrentOverView(result.cityOverView)
          }
        })
        .catch(() => {
          dispatch({
            type: actionTypes.SET_LOADING,
            data: false
          })
          let result: PhotoAddrStatisticResp = {
            cityOverView: {} as AreaOverView,
            districtsStatistic: []
          }
          setMapData(result)
        })
    },
    [dispatch, isVip, handleChangeFilter, groupId]
  )

  // 点击地图气泡
  const handleClickMarker = useCallback(
    (markerData: AreaOverView | POIItemOverView, extra?: any) => {
      sensorsClick('locationBubble', markerData.name)
      // 非会员点击地区气泡，去提示开通Vip
      /*if (markerData.level === 'district' && !isVip) {
        setOpenVipModalVisible(true)
        sensors.track('web_show_page_openapp_workgroupvip', {
          groupID: groupId,
          pagePath: 'photoLocation',
          functionType: 'checkLocationDetail'
        })
        return
      }*/
      markerData.extra = extra
      setCurrentOverView(markerData)
      if (markerData.level === 'poi') {
        setPoiDetailCardVisible(true)
      }
    },
    [sensorsClick, isVip, groupId]
  )

  // 渲染概览模板数据头
  const renderPanelHeader = useCallback(() => {
    return (
      <div className="header-text">
        <span>{currentOverView.name}</span>
        <span>
          {filterData.times.start.format('M.DD') +
            '-' +
            filterData.times.end.format('M.DD')}
        </span>
      </div>
    )
  }, [currentOverView, filterData])

  // 渲染概览模板数据内容
  const renderPanelBody = useCallback(() => {
    const { photoAddrCnt, subDistrictCnt, photoUserCnt, photoCnt, level } =
      currentOverView
    const isDistrict = level === 'district'
    return (
      <div className="body-content">
        <div className="desc">
          <span>地点总数</span>
          <span>
            {convertMaxNumberStr(photoAddrCnt, 9999)}
            <span>个</span>
          </span>
        </div>
        {!isDistrict && (
          <div className="desc">
            <span>区县数</span>
            <span>
              {convertMaxNumberStr(subDistrictCnt, 99)}
              <span>个</span>
            </span>
          </div>
        )}
        <div className="desc">
          <span>拍照成员</span>
          <span>
            {convertMaxNumberStr(photoUserCnt, 999)}
            <span>人</span>
          </span>
        </div>
        <div className="desc">
          <span>照片数</span>
          <span>
            {convertMaxNumberStr(photoCnt, 99999)}
            <span>张</span>
          </span>
        </div>
      </div>
    )
  }, [currentOverView])

  return (
    <Layout className="photo-location-container">
      <PageHeader
        className="Header"
        ghost={false}
        title="照片地点"
        extra={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* 查看人员实时定位按钮 */}
            {showMemberLocation && (
              <div
                style={{ marginBottom: 10, marginRight: 20, cursor: 'pointer' }}
                onClick={() => {
                  navigate('/member/location')
                  sensors.track('web_click_page_photo_location', {
                    groupID: groupId,
                    clickItem: 'realTimeLocation',
                    locationName: '拍照地点',
                    track_sign:
                      'aworkgroup.b7608.action.web_click_page_photo_location',
                    requirement_id: 322
                  })
                }}
              >
                <img
                  style={{ height: '40px' }}
                  src={member_location_entry_btn}
                />
              </div>
            )}

            {/* 联系我们按钮 */}
            <ContactUS fromPage="拍照地点">
              <span
                className="contact-item"
                onClick={() => sensorsClick('contactUs')}
              >
                <img style={{ width: '105px' }} src={contact_us_icon} />
              </span>
            </ContactUS>
          </div>
        }
      />
      <Content className="content">
        <Filter
          handleChangeFilter={handleChangeFilter}
          filterData={filterData}
        />
        {/*<div>
          动态调整数量：
          <InputNumber
            value={maxPoint}
            onChange={(val) => setMaxPoint(val || 0)}
          />
        </div>*/}
        <MapContainer
          // maxPoint={maxPoint}
          mapData={mapData}
          handleClickMarker={handleClickMarker}
          setCurrentOverView={setCurrentOverView}
        />
        {currentOverView.level && currentOverView.level !== 'poi' && (
          <div className="overview-card">
            <Collapse
              bordered={false}
              defaultActiveKey={1}
              className="overview-card-collapse"
              onChange={(activeKeys) => {
                // 展开/收起
                sensorsClick(activeKeys.length ? 'expandPrompt' : 'stowPrompt')
              }}
              expandIcon={({ isActive }) => {
                return <DownOutlined rotate={isActive ? 180 : 0} />
              }}
              expandIconPosition={'end'}
            >
              <Panel header={renderPanelHeader()} key="1">
                {renderPanelBody()}
              </Panel>
            </Collapse>
          </div>
        )}
        <POIDetailCard
          visible={poiDetailCardVisible}
          setVisible={setPoiDetailCardVisible}
          filterData={filterData}
          sensorsClick={sensorsClick}
          handleClose={() => {
            sensorsClick('detailsReturn')
            setPoiDetailCardVisible(false)
            setTimeout(() => {
              setCurrentOverView(prevOverView.current)
            }, 300)
          }}
          currentOverView={currentOverView as POIItemOverView}
        />
        {/*{!isVip && (
          <OpenVipModal
            visible={openVipModalVisible}
            setVisible={setOpenVipModalVisible}
          />
        )}*/}
      </Content>
    </Layout>
  )
}

export default PhotoLocation
